/**
 * Green Flag — ModeSwitch
 *
 * The two-segment switch between the member app and a staff workspace: "App ·
 * Admin" for admins, "App · Check-in" for hosts. It sits at the top right of
 * the global nav in both modes, so it stays put when the mode changes: light
 * in the member app, dark on the workspace's forest top bar.
 *
 * Each segment is a link, not a toggle. The other side's opens the page the
 * person was last on there (see `lastPath`); the current side's stays on this
 * page. The current one carries `aria-current="page"`, which one style object
 * keys off, as in PillNav.
 */

import { Box, Flex, type SystemStyleObject } from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Icon } from "../Icon/Icon";
import { useStoreData } from "../../lib/store/store";
import { adminSegmentStyles } from "../../lib/theme/adminStyles";
import { lastPath, MODES, type Workspace } from "../../lib/useWorkspace";

/**
 * A segment is drawn 26px tall, but a thumb needs 44: the hit area reaches
 * 9px above and below it. Only vertically, so the two segments never overlap.
 */
const HIT_AREA: SystemStyleObject = {
  position: "relative",
  _before: {
    content: '""',
    position: "absolute",
    insetInline: "0",
    insetBlock: "-9px",
  },
};

const segmentStyles = (dark: boolean): SystemStyleObject => ({
  ...HIT_AREA,
  ...adminSegmentStyles(dark),
  h: "26px",
  px: "3",
  textDecoration: "none",
});

export interface ModeSwitchProps {
  /** The workspace the second segment opens. */
  workspace: Workspace;
  /**
   * The side the page is on. It lights that segment and sets the tone: light
   * in the app's nav, dark on the workspace's top bar.
   */
  side: "app" | "workspace";
}

export const ModeSwitch = ({ workspace, side }: ModeSwitchProps) => {
  const { pathname, search } = useLocation();
  const userId = useStoreData().user?.id;
  const dark = side === "workspace";
  const current = dark ? workspace : "app";
  const styles = segmentStyles(dark);

  return (
    <Flex
      role="group"
      aria-label="Switch workspace"
      align="center"
      gap="0.5"
      h="8"
      p="3px"
      flexShrink={0}
      borderRadius="pill"
      bg={dark ? "admin.chrome.track" : "bg.subtle"}
      boxShadow={dark ? undefined : "inset 0 0 0 1px {colors.border.subtle}"}
    >
      {(["app", workspace] as const).map((mode) => (
        <Box asChild key={mode} css={styles}>
          <RouterLink
            to={mode === current ? pathname + search : lastPath(userId, mode)}
            aria-current={mode === current ? "page" : undefined}
          >
            <Icon name={MODES[mode].icon} boxSize="3" />
            {MODES[mode].label}
          </RouterLink>
        </Box>
      ))}
    </Flex>
  );
};
